import React, { useEffect, useState } from 'react';
import {
  Header,
  Icon,
  Loader,
  Message,
  Segment,
  Select,
  Statistic,
  Table,
} from 'semantic-ui-react';
import { getUserMetrics } from '../../services/Metrics';
import { getAllUsers } from '../../services/Settings';
import { MONTH_OPTIONS, getCurrentPeriod } from './metricsHelpers';

function MetricsUserSection() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [state, setState] = useState({ data: null, loading: false, error: false });

  useEffect(() => {
    getAllUsers()
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          setUsers(res.data.result ?? []);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedUser) return;
    setState({ data: null, loading: true, error: false });
    getUserMetrics(selectedUser, period)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          setState({ data: res.data.result, loading: false, error: false });
        } else {
          setState({ data: null, loading: false, error: true });
        }
      })
      .catch(() => setState({ data: null, loading: false, error: true }));
  }, [selectedUser, period]);

  const userOptions = users.map((u) => ({
    key: u.id,
    value: u.id,
    text: u.nickname ?? u.email ?? `User #${u.id}`,
  }));

  return (
    <Segment className="transparent-table">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1em',
          flexWrap: 'wrap',
          gap: '0.5em',
        }}
      >
        <Header as="h3" color="teal" style={{ margin: 0 }}>
          <Icon name="user circle" />
          <Header.Content>Métriques utilisateur</Header.Content>
        </Header>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <Select
            placeholder="Choisir un utilisateur"
            options={userOptions}
            value={selectedUser}
            onChange={(_, { value }) => setSelectedUser(value)}
            compact
          />
          <Select
            options={MONTH_OPTIONS}
            value={period}
            onChange={(_, { value }) => setPeriod(value)}
            compact
          />
        </div>
      </div>
      {!selectedUser && (
        <Message info content="Sélectionnez un utilisateur pour voir ses statistiques." />
      )}
      {selectedUser && state.loading && <Loader active inline="centered" />}
      {selectedUser && state.error && (
        <Message error content="Impossible de charger les métriques de cet utilisateur." />
      )}
      {selectedUser &&
        !state.loading &&
        state.data &&
        (() => {
          const d = state.data;
          const totalSessions = d.session_start?.count ?? 0;
          const totalDownloads = (d.download_browser?.count ?? 0) + (d.download_device?.count ?? 0);
          const totalTorrents = Object.entries(d)
            .filter(([k]) => k.startsWith('torrent_add_'))
            .reduce((acc, [, v]) => acc + v.count, 0);
          const rows = Object.entries(d).sort((a, b) => b[1].count - a[1].count);
          return (
            <>
              <Statistic.Group widths={3} size="mini" style={{ marginBottom: '1.5em' }}>
                <Statistic>
                  <Statistic.Value>{totalSessions}</Statistic.Value>
                  <Statistic.Label>Sessions</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{totalTorrents}</Statistic.Value>
                  <Statistic.Label>Torrents ajoutés</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{totalDownloads}</Statistic.Value>
                  <Statistic.Label>Téléchargements</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              {rows.length > 0 && (
                <Table basic="very" unstackable compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Événement</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Occurrences</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map(([name, v]) => (
                      <Table.Row key={name}>
                        <Table.Cell>{name}</Table.Cell>
                        <Table.Cell textAlign="right">{v.count}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </>
          );
        })()}
    </Segment>
  );
}

export default MetricsUserSection;
